import { useRef, useState } from "react";

/* Soltar archivos: arrastrarlos o elegirlos, y ver cuáles quedaron.
 *
 * ── LAS DOS PUERTAS, Y NO UNA ────────────────────────────────────────────────
 *
 * Pedido de Igna (25/08/2026): «drag and drop Y seleccionar archivo». Son las
 * dos porque son dos personas distintas: el que ya tiene el explorador abierto
 * arrastra, y el que está en el celular —o el que nunca arrastró un archivo en
 * su vida— aprieta un botón. Ofrecer sólo una deja afuera a la mitad de RRHH.
 *
 * Por eso el recuadro entero es un `<label>`: arrastrar y hacer clic llevan al
 * mismo lugar sin que haya que explicar cuál es cuál.
 *
 * ── NO SE CONVIERTE NADA ─────────────────────────────────────────────────────
 *
 * Entra lo que sea y sale igual. El PDF de un certificado o de un telegrama ES
 * el registro: tiene la firma, el membrete y el sello, y pasarlo a texto o
 * recomprimirlo le saca justo lo que le da valor.
 *
 * ── LO QUE NO ENTRÓ SE DICE ──────────────────────────────────────────────────
 *
 * Un archivo más grande que el tope no se descarta en silencio: se nombra y se
 * dice por qué. Un adjunto que la persona cree que subió y no está es peor que
 * uno que se rechazó a la vista — es la misma regla de siempre acá, un dato
 * que falta tiene que verse.
 */

/* 25 MB. Un escaneo de dos carillas anda por 1–3 MB; una foto de celular
 * moderna, por 5. El tope está para atajar el video que alguien suelta sin
 * querer, no para pelear con un PDF grande. */
export const TOPE_BYTES = 25 * 1024 * 1024;

export const enMB = (b) => `${(Number(b || 0) / 1024 / 1024).toFixed(1)} MB`;

/* ¿El archivo es del tipo que se pide? `accept` como el del input
 * («application/pdf», «.pdf,image/*»). Sin `accept`, entra todo. Se mira el
 * tipo y también la extensión: hay celulares que mandan un PDF sin tipo. */
export function esDelTipo(f, accept) {
  if (!accept) return true;
  const tipo = String(f?.type || "").toLowerCase();
  const nombre = String(f?.name || "").toLowerCase();
  return String(accept).split(",").map((x) => x.trim().toLowerCase()).filter(Boolean)
    .some((a) => (a.startsWith(".") ? nombre.endsWith(a)
      : a.endsWith("/*") ? tipo.startsWith(a.slice(0, -1))
      : tipo === a || (a === "application/pdf" && nombre.endsWith(".pdf"))));
}

/* Separa lo que entra de lo que no, y dice por qué no. Puro, se prueba solo. */
export function filtrar(archivos, tope = TOPE_BYTES, accept = "") {
  const entran = [];
  const rechazados = [];
  for (const f of archivos || []) {
    if (!f) continue;
    if (!esDelTipo(f, accept)) {
      rechazados.push({ nombre: f.name, motivo: accept === "application/pdf"
        ? "no es un PDF" : "no es del tipo que se pide" });
    } else if ((f.size ?? 0) > tope) {
      rechazados.push({ nombre: f.name, motivo: `pesa ${enMB(f.size)}` });
    } else if ((f.size ?? 0) === 0) {
      // Un archivo de cero bytes casi siempre es un arrastre fallido o una
      // carpeta. Subirlo deja un adjunto que no se puede abrir.
      rechazados.push({ nombre: f.name, motivo: "está vacío" });
    } else {
      entran.push(f);
    }
  }
  return { entran, rechazados };
}

/* Los demás props son para usarlo con UN archivo de un tipo (la OT de una
 * garantía: Nico, 05/10, «que sea drag & drop ahí también»): `multiple`,
 * `accept`, el texto del recuadro, y `elegido` —el archivo que ya quedó— para
 * mostrarlo con «Quitar» en vez del «ningún archivo seleccionado» del
 * navegador. Sin esos props se comporta como siempre. */
export default function SoltarArchivos({
  onArchivos,
  deshabilitado = false,
  tope = TOPE_BYTES,
  multiple = true,
  accept = "",
  titulo = "Arrastrá archivos acá",
  detalle = null,
  elegido = null,
  onQuitar = null,
  etiqueta = undefined,
}) {
  const input = useRef(null);
  const [encima, setEncima] = useState(false);
  const [rechazados, setRechazados] = useState([]);

  const recibir = (lista) => {
    const { entran, rechazados: fuera } = filtrar(Array.from(lista || []), tope, accept);
    setRechazados(fuera);
    if (entran.length) onArchivos?.(multiple ? entran : entran.slice(0, 1));
  };

  return (
    <div className="soltar-archivos">
      <label
        className={`soltar-caja${encima ? " encima" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          if (!deshabilitado) setEncima(true);
        }}
        onDragLeave={() => setEncima(false)}
        onDrop={(e) => {
          e.preventDefault();
          setEncima(false);
          if (!deshabilitado) recibir(e.dataTransfer?.files);
        }}
      >
        <input
          ref={input}
          type="file"
          multiple={multiple}
          accept={accept || undefined}
          aria-label={etiqueta}
          disabled={deshabilitado}
          onChange={(e) => {
            recibir(e.target.files);
            // Se limpia para que soltar DOS VECES el mismo archivo vuelva a
            // disparar el change. Sin esto, el segundo intento no hace nada y
            // parece que la pantalla se colgó.
            e.target.value = "";
          }}
          className="soltar-input"
        />
        <span className="soltar-titulo">{titulo}</span>
        <span className="sub">
          {detalle ?? `o hacé clic para elegirlos · cualquier formato, hasta ${enMB(tope)}`}
        </span>
      </label>

      {elegido && (
        <p className="sub" style={{ margin: "0.4rem 0 0" }}>
          📎 <b>{elegido.name}</b> ({enMB(elegido.size)})
          {onQuitar && (
            <>
              {" "}
              <button type="button" className="btn btn--ghost" disabled={deshabilitado}
                onClick={onQuitar}>
                Quitar
              </button>
            </>
          )}
        </p>
      )}

      {rechazados.length > 0 && (
        <ul className="soltar-rechazados">
          {rechazados.map((r, i) => (
            <li key={`${r.nombre}-${i}`}>
              <b>{r.nombre}</b>: no se subió porque {r.motivo}.
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
